import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { Capacitor } from '@capacitor/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { PageRequest } from '../entities/pagerequest.model';
import { CrashlyticsService } from '../shared/crashlytics.service';
import { LoggingService } from '../shared/logging.service';
import { OfflineNotificationService } from './offline-notification.service';
import { UserEventWriteService } from './user-event.write.service';

@Injectable({ providedIn: 'root' })
export abstract class AbstractReadQueryService<T> {
  constructor(
    private readonly http: HttpClient,
    private readonly offlineService: OfflineNotificationService,
    private readonly userEventWriteService: UserEventWriteService,
    private readonly log: LoggingService,
    private readonly crashlytics: CrashlyticsService
  ) {}

  public fetch(queryParam: any = null, urlParam: any = null): Observable<T> {
    // FIRST : try to write
    // refresh the internal value if possible, then return the internal value
    return this.userEventWriteService.tryToRefresh().pipe(switchMap(() => this.getValue(queryParam, urlParam)));
  }

  private getValue(queryParam: any = null, params: any = null): Observable<any> {
    return this.tryToGetFromServer(queryParam, params).pipe(
      tap(() => this.offlineService.setOnline()),
      catchError(err => {
        this.log.log('Read from server failed : status (' + typeof err.status + ') ' + err.status);
        if (err.status === 404) {
          // server not found = novalue
          this.offlineService.setOnline();
        } else {
          // on est offline
          this.offlineService.setOffline();
        }
        try {
          this.reportError(err, queryParam, params);
        } catch (e) {
          this.log.warn('error in report');
        }
        // everything is allright
        return of({});
      })
    );
  }

  private tryToGetFromServer(queryParam: any = null, urlParam: any = null): Observable<T> {
    return this.http.get<T>(this.getResourceURL(urlParam), { observe: 'response', params: queryParam }).pipe(
      map(res => res.body),
      map(res => this.hydrate(res))
    );
  }

  protected abstract getResourceURL(urlParam: any): string;

  protected abstract hydrate(obj: any): T;

  private async reportError(error: any, pageRequest: PageRequest, param: any): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      this.log.warn('error[', error, ']');
      if (typeof error === 'string') {
        return FirebaseCrashlytics.recordException({
          message: error,
        }).then(() => FirebaseCrashlytics.sendUnsentReports());
      } else if (error instanceof HttpErrorResponse) {
        return FirebaseCrashlytics.recordException({
          message: error.message,
        }).then(() => FirebaseCrashlytics.sendUnsentReports());
      } else {
        this.crashlytics.sendStackTrace(error);
      }
    } else {
      this.log.error('Un erreur est survenue durant la lecture de données via AbstractReadQueryService', error);
    }
  }

  private serializeParams(params: any): string {
    const parts = [];
    for (const i of params) {
      parts.push(encodeURIComponent(i) + '=' + encodeURIComponent(params[i]));
    }
    return parts.join('&');
  }
}
